import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "reminder-mail-modal__description" }
const _hoisted_2 = { class: "reminder-mail-status" }
const _hoisted_3 = { class: "reminder-mail-status__label" }
const _hoisted_4 = { class: "reminder-mail-status__label reminder-mail-status__label--set" }
const _hoisted_5 = { class: "reminder-mail-status__date" }
const _hoisted_6 = { class: "reminder-mail-form" }
const _hoisted_7 = { class: "reminder-mail-form__field" }
const _hoisted_8 = { class: "reminder-mail-form__memo-header" }
const _hoisted_9 = { class: "reminder-mail-form__label" }
const _hoisted_10 = ["placeholder"]
const _hoisted_11 = { class: "reminder-mail-form__footer" }
const _hoisted_12 = { class: "reminder-mail-form__character-count" }
const _hoisted_13 = { class: "reminder-mail-form__field" }
const _hoisted_14 = { class: "reminder-mail-form__date-header" }
const _hoisted_15 = { class: "reminder-mail-form__label" }
const _hoisted_16 = { class: "reminder-mail-form__date-row" }
const _hoisted_17 = { class: "reminder-mail-form__field" }
const _hoisted_18 = { class: "reminder-mail-form__label" }

import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { i18n } from '@/i18n';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import MultipleSelectInput from '@/corePagesModule/SelectInput/MultipleSelectInput.vue';
import AppDatePicker from '@/corePagesModule/components/AppDatePicker.vue';
import { ReminderMailData } from '../useCases/saveReminderMailSettingsUseCase';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import ErrorMessage from '@/corePagesModule/components/ErrorMessage.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';

/**
 * リマインダーメール設定モーダルコンポーネント
 */
/**
 * コンポーネントのprops定義
 */
interface Props {
  /** モーダルの表示状態 */
  modalState?: boolean;
  /** 発注ID */
  orderId?: string;
  /** 既存メッセージ（任意） */
  initialMessage?: string;
  /** 既存リマインド日 YYYYMMDD（任意） */
  initialReminderDate?: string | null;
  /** 既存の受信者（任意） */
  initialRecipients?: string[];
}

// eslint-disable-next-line jsdoc/require-jsdoc
const MAX_MESSAGE_LENGTH = 255;

// 最小選択日付（9時以降は翌日0時、それ以前は当日0時）

export default /*@__PURE__*/_defineComponent({
  __name: 'ReminderMailModal',
  props: {
    modalState: { type: Boolean, default: false },
    orderId: { default: '' },
    initialMessage: { default: '' },
    initialReminderDate: { default: null },
    initialRecipients: { default: () => [] }
  },
  emits: ["closeModal", "saveReminderMail", "deleteReminderMail"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// emits
const emits = __emit;

// 変数
const t = i18n.t;
const locale = computed(() => i18n.locale);
const minDate = ref<Date>(new Date());

/**
 * 指定した日付の0時を取得する
 * @param d - 日付
 */
const startOfDay = (d: Date): Date => {
  const res = new Date(d);
  res.setHours(0, 0, 0, 0);
  return res;
};

/**
 * 指定した日付の0時を取得する
 */
const startOfTomorrow = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * 最小選択日付を更新する（9時以降は翌日0時、それ以前は当日0時）
 */
const updateMinDate = () => {
  const now = new Date();
  const nineAM = new Date();
  nineAM.setHours(9, 0, 0, 0);
  const newMin = now.getTime() > nineAM.getTime() ? startOfTomorrow() : startOfDay(now);
  minDate.value = newMin;

  // 現在選択中の日付が範囲外になった場合はクリア
  if (selectedDate.value && selectedDate.value < newMin) {
    selectedDate.value = null;
    fieldState['reminderDate'].setValue('');
  }
};

// 状態: 初期リマインド日の有無で表示を切替
const isReminderSet = computed(() => !!props.initialReminderDate);

// 表示用に YYYYMMDD -> YYYY/MM/DD へ整形
const formattedReminderDate = computed(() => {
  const v = props.initialReminderDate;
  if (!v || v.length !== 8) return '';
  const y = v.slice(0, 4);
  const m = v.slice(4, 6);
  const d = v.slice(6, 8);
  return `${y}/${m}/${d}`;
});

// フォームフィールド定義
const fieldItems = computed((): FieldItem[] => {
  return [
    {
      name: 'message',
      rules: [ValidateRule.Required, ValidateRule.MemoInvalidChar, ValidateRule.MemoTooMuchLength],
    },
    {
      name: 'reminderDate',
      rules: [ValidateRule.Required],
    },
    {
      name: 'recipients',
      rules: [ValidateRule.Required],
    },
  ];
});

// useFormを使用してフォーム管理
const { fieldState, allValidate, resetForm, isAllValid } = useForms(fieldItems.value);

// 日付フィールドのための追加の状態
const selectedDate = ref<Date | null>(null);

// 受信者を独立して管理
const recipients = ref<string[]>([]);

// 組織ユーザーリスト
const organizationUsers = ref<string[]>([]);

// 文字数カウント
const messageLength = computed(() => fieldState['message'].value.value.length);
const messageCountText = computed(() => `${messageLength.value}/${MAX_MESSAGE_LENGTH}`);

/**
 * メッセージ入力時の処理
 * @param event - 入力イベント
 */
const handleMessageInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  if (target) {
    fieldState['message'].setValue(target.value);
  }
};

/**
 * リマインダー日付をリセットする
 */
const resetReminderDate = () => {
  selectedDate.value = null;
  fieldState['reminderDate'].setValue('');
};

/**
 * 日付変更時の処理
 * @param date - 選択された日付
 */
const handleDateChange = (date: Date | null) => {
  selectedDate.value = date;
  if (date) {
    // ローカルタイムでYYYY-MM-DDに整形（UTCシフト防止）
    const dateString = DateService.convertDateStrToDateWithHyphen(date);
    fieldState['reminderDate'].setValue(dateString);
  } else {
    fieldState['reminderDate'].setValue('');
  }
};

/**
 * 受信者変更時の処理
 * @param selectedUsers - 選択されたユーザー配列
 */
const handleRecipientsChange = (selectedUsers: string[]) => {
  const recipientsString = selectedUsers.join(',');
  fieldState['recipients'].setValue(recipientsString);
};

/**
 * 受信者の現在選択されている配列を取得
 */
const currentRecipients = computed(() => {
  const value = fieldState['recipients'].value.value;
  return value ? value.split(',').filter((r) => r.trim() !== '') : [];
});

// watchの停止関数を格納する配列
const watchStopHandlers: Array<() => void> = [];

// recipientsの変更を監視してfieldStateを更新（メモリリーク対策）
const stopRecipientsWatch = watch(
  recipients,
  (newRecipients) => {
    const recipientsString = newRecipients.length > 0 ? newRecipients.join(',') : '';
    fieldState['recipients'].setValue(recipientsString);
  },
  { deep: true }
);

// watch停止関数を配列に追加
watchStopHandlers.push(stopRecipientsWatch);

/**
 * YYYYMMDD形式の文字列をDateに変換する
 * @param yyyymmdd - 変換対象の日付文字列（YYYYMMDD）
 * @returns 変換後のDate。無効な場合はnull
 */
const parseYYYYMMDDToDate = (yyyymmdd: string): Date | null => {
  if (!yyyymmdd || yyyymmdd.length !== 8) return null;
  const y = Number(yyyymmdd.slice(0, 4));
  const m = Number(yyyymmdd.slice(4, 6));
  const d = Number(yyyymmdd.slice(6, 8));
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

/**
 * モーダルのフォームに初期値を適用する
 */
const applyInitialValues = () => {
  // メッセージ
  fieldState['message'].setValue(props.initialMessage ?? '');

  // 日付
  const parsed = props.initialReminderDate ? parseYYYYMMDDToDate(props.initialReminderDate) : null;
  selectedDate.value = parsed;
  if (parsed) {
    // ローカルタイムでYYYY-MM-DDに整形（UTCシフト防止）
    const dateString = DateService.convertDateStrToDateWithHyphen(parsed);
    fieldState['reminderDate'].setValue(dateString);
  } else {
    fieldState['reminderDate'].setValue('');
  }

  // 受信者
  recipients.value = Array.isArray(props.initialRecipients) ? [...props.initialRecipients] : [];
};

/**
 * モーダルを閉じる
 */
const closeModal = () => {
  resetFormData();
  emits('closeModal');
};

/**
 * リマインダーメール設定を解除（削除）する
 */
const deleteReminderMail = () => {
  emits('deleteReminderMail');
  closeModal();
};

/**
 * リマインダーメール設定を保存する
 */
const saveReminderMail = () => {
  if (allValidate()) {
    const reminderMailData: ReminderMailData = {
      orderId: props.orderId || '',
      message: fieldState['message'].value.value,
      reminderDate: fieldState['reminderDate'].value.value,
      recipients: recipients.value,
    };
    emits('saveReminderMail', reminderMailData);
    closeModal();
  }
};

/**
 * フォームをリセットする
 */
const resetFormData = () => {
  resetForm(fieldItems.value);
  selectedDate.value = null;
  recipients.value = [];
};

// モーダルが開いたら初期値を反映
watch(
  () => props.modalState,
  (isOpen) => {
    if (isOpen) {
      resetFormData();
      updateMinDate();
      applyInitialValues();
    }
  }
);

// 初期マウント時にも最小日付を設定
onMounted(() => {
  updateMinDate();
});

/**
 * コンポーネント破棄時のクリーンアップ処理
 */
onBeforeUnmount(() => {
  // 全てのwatchを停止してメモリリークを防ぐ
  watchStopHandlers.forEach((stopHandler) => {
    stopHandler();
  });
  watchStopHandlers.length = 0;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(InfoModal, {
    "modal-state": _ctx.modalState,
    "enable-outside-click": true,
    "back-button-label": _unref(t)('キャンセル'),
    "next-button-label": _unref(t)('設定する'),
    "is-disabled-next-button": !_unref(isAllValid),
    onOnNext: saveReminderMail,
    onOnBack: closeModal
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('リマインドメール設定')), 1)
    ]),
    description: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, _toDisplayString(_unref(t)('メモ内容を編集し、リマインド日を設定してください。')), 1)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        (!isReminderSet.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(Mdi, {
                class: "reminder-mail-status__icon",
                icon: "calendar_today"
              }),
              _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('未設定')), 1)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('設定済み')), 1),
              _createElementVNode("span", _hoisted_5, "(" + _toDisplayString(formattedReminderDate.value) + ")", 1),
              _createElementVNode("span", {
                class: "reminder-mail-status__unlink",
                role: "button",
                tabindex: "0",
                onClick: deleteReminderMail
              }, _toDisplayString(_unref(t)('リマインドメール解除')), 1)
            ], 64))
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", _hoisted_9, _toDisplayString(_unref(t)('メモ内容')), 1)
          ]),
          _withDirectives(_createElementVNode("textarea", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(fieldState)['message'].value.value) = $event)),
            placeholder: _unref(t)('255文字以内で記入してください'),
            class: "reminder-mail-form__textarea",
            rows: "6",
            maxlength: MAX_MESSAGE_LENGTH,
            onInput: handleMessageInput
          }, null, 40, _hoisted_10), [
            [_vModelText, _unref(fieldState)['message'].value.value]
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createVNode(ErrorMessage, {
              "is-error": _unref(fieldState)['message'].isError.value,
              "error-message": _unref(fieldState)['message'].errorMessage.value,
              class: "reminder-mail-form__error"
            }, null, 8, ["is-error", "error-message"]),
            _createElementVNode("div", _hoisted_12, _toDisplayString(messageCountText.value), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("div", _hoisted_14, [
            _createElementVNode("label", _hoisted_15, _toDisplayString(_unref(t)('リマインド日')), 1),
            _createElementVNode("button", {
              type: "button",
              class: "reminder-mail-form__reset-button",
              onClick: resetReminderDate
            }, _toDisplayString(_unref(t)('リセット')), 1)
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createVNode(AppDatePicker, {
              modelValue: selectedDate.value,
              "onUpdate:modelValue": [
                _cache[1] || (_cache[1] = ($event: any) => ((selectedDate).value = $event)),
                handleDateChange
              ],
              class: "reminder-mail-form__date-picker",
              format: 'yyyy-MM-dd',
              locale: locale.value,
              placeholder: _unref(t)('未設定'),
              clearable: false,
              "enable-time-picker": false,
              "min-date": minDate.value,
              teleport: true,
              placement: 'top',
              "auto-apply": ""
            }, null, 8, ["modelValue", "locale", "placeholder", "min-date"])
          ]),
          _createVNode(ErrorMessage, {
            "is-error": _unref(fieldState)['reminderDate'].isError.value,
            "error-message": _unref(fieldState)['reminderDate'].errorMessage.value,
            class: "reminder-mail-form__error"
          }, null, 8, ["is-error", "error-message"])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("label", _hoisted_18, _toDisplayString(_unref(t)('リマインド受信者')), 1),
          _createVNode(MultipleSelectInput, {
            "selected-list": recipients.value,
            index: 0,
            "filter-type": 'STAFF_NAME',
            "prop-options": organizationUsers.value,
            placeholder: _unref(t)('選択してください'),
            label: '',
            class: "reminder-mail-form__multi-select",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = (e) => (recipients.value = e))
          }, null, 8, ["selected-list", "prop-options", "placeholder"]),
          _createVNode(ErrorMessage, {
            "is-error": _unref(fieldState)['recipients'].isError.value,
            "error-message": _unref(fieldState)['recipients'].errorMessage.value,
            class: "reminder-mail-form__error"
          }, null, 8, ["is-error", "error-message"])
        ])
      ])
    ], undefined, true),
    _: 1
  }, 8, ["modal-state", "back-button-label", "next-button-label", "is-disabled-next-button"]))
}
}

})